import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar } from '../components/ui/calendar';
import { fetchPrograms, fetchTrainers, fetchSlots, createBooking } from '../services/api';
import { Clock, Check, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { fr } from 'date-fns/locale';

const BookingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDirect = searchParams.get('direct') === 'true';
  const directProgramId = searchParams.get('program');
  const directFormula = searchParams.get('formula');
  const directType = searchParams.get('type');
  const isKitBooking = isDirect && (!directFormula || directFormula === 'kit');
  const isOralBlanc = directType === 'oral-blanc';
  const isPresentation = directType === 'presentation';

  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [step, setStep] = useState(1);
  const [VAE_PROGRAMS, setPrograms] = useState([]);
  const [MOCK_TRAINERS, setTrainers] = useState([]);
  const [MOCK_AVAILABLE_SLOTS, setSlots] = useState([]);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  // Candidate info form
  const [candidateInfo, setCandidateInfo] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    livret1Depose: '',
    redactionDebutee: '',
    besoin: ''
  });
  const [candidateInfoSubmitted, setCandidateInfoSubmitted] = useState(false);

  useEffect(() => {
    Promise.all([
      fetchPrograms().then(setPrograms),
      fetchTrainers().then(setTrainers),
      fetchSlots().then(setSlots)
    ]).catch(console.error).finally(() => setPageLoading(false));
  }, []);

  // Direct mode: auto-fill candidate info from logged-in user and skip to calendar
  useEffect(() => {
    if (isDirect && directProgramId) {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user.email) {
          const info = {
            nom: user.name || '',
            prenom: user.firstname || '',
            email: user.email || '',
            telephone: user.telephone || user.phone || '',
            livret1Depose: 'Oui',
            redactionDebutee: 'Oui',
            besoin: 'Appel inclus dans mon kit'
          };
          setCandidateInfo(info);
          setSelectedProgram(directProgramId);
          // Only skip form if all required fields are filled (especially telephone)
          if (info.nom && info.prenom && info.email && info.telephone) {
            setCandidateInfoSubmitted(true);
            setStep(2);
          } else {
            setStep(1);
          }
        }
      } catch {}
    }
  }, [isDirect, directProgramId]);

  const getMinimumDate = () => {
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + 2);
    minDate.setHours(0, 0, 0, 0);
    return minDate;
  };
  const minimumDate = getMinimumDate();

  const handleProgramSelect = (programId) => {
    setSelectedProgram(programId);
    setStep(2);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setStep(3);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const handleBooking = async () => {
    if (!selectedProgram || !selectedDate || !selectedSlot) {
      toast.error('Veuillez remplir toutes les étapes');
      return;
    }

    setBookingLoading(true);
    try {
      const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
      const autoTrainerId = MOCK_TRAINERS.length > 0 ? MOCK_TRAINERS[0].id : '';
      const result = await createBooking({
        programId: selectedProgram,
        trainerId: autoTrainerId,
        date: dateStr,
        time: selectedSlot.time,
        candidateInfo: candidateInfo
      });

      setBookingConfirmed(result);
      toast.success('Séance réservée avec succès !');
    } catch (error) {
      console.error('Booking error:', error);
      if (error.message.includes('déjà réservé')) {
        toast.error('Ce créneau vient d\'être réservé. Choisissez un autre horaire.');
        fetchSlots().then(setSlots).catch(console.error);
      } else if (error.message.includes('appel gratuit')) {
        toast.error(error.message);
      } else {
        toast.error(error.message || 'Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setBookingLoading(false);
    }
  };

  const getAvailableSlotsForDate = () => {
    if (!selectedDate) return [];
    const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
    return MOCK_AVAILABLE_SLOTS.filter(slot => slot.date === dateStr && slot.available);
  };

  const program = VAE_PROGRAMS.find(p => p.id === selectedProgram);

  // Booking confirmed view
  if (bookingConfirmed) {
    return (
      <div style={{ paddingTop: '80px', minHeight: '100vh' }}>
        <section style={{ 
          background: 'var(--gradient-hero-warm)',
          padding: '4rem 1.2rem 2rem',
          textAlign: 'center'
        }}>
          <div className="container" style={{ maxWidth: '600px' }}>
            <div style={{ 
              width: 80, height: 80, borderRadius: '50%', background: '#f0fdf4', 
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem'
            }}>
              <CheckCircle size={40} style={{ color: '#16a34a' }} />
            </div>
            <h1 className="heading-hero" style={{ marginBottom: '1rem', color: '#16a34a' }}>
              Séance confirmée !
            </h1>
            <p className="body-large" style={{ marginBottom: '2rem' }}>
              {isOralBlanc 
                ? 'Votre oral blanc de 1h15 a été réservé avec succès.'
                : isPresentation
                ? 'Votre RDV de présentation personnelle a été réservé avec succès.'
                : `Votre créneau de ${isKitBooking ? '15' : '20'} minutes gratuit a été réservé avec succès.`
              }
            </p>
          </div>
        </section>
        <section style={{ padding: '2rem 1.2rem' }}>
          <div className="container" style={{ maxWidth: '500px' }}>
            <div className="card" style={{ padding: '2rem' }} data-testid="booking-confirmation">
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>Récapitulatif</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-light)', paddingBottom: '0.75rem' }}>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Formation</span>
                  <span className="body-small" style={{ fontWeight: 600 }}>{bookingConfirmed.program}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-light)', paddingBottom: '0.75rem' }}>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Date</span>
                  <span className="body-small" style={{ fontWeight: 600 }}>{new Date(bookingConfirmed.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-light)', paddingBottom: '0.75rem' }}>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Heure</span>
                  <span className="body-small" style={{ fontWeight: 600 }}>{bookingConfirmed.time}</span>
                </div>
                {!isKitBooking && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Séance n°</span>
                  <span className="body-small" style={{ fontWeight: 600 }}>{bookingConfirmed.sessionNumber}</span>
                </div>
                )}
                {isKitBooking && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Durée</span>
                  <span className="body-small" style={{ fontWeight: 600 }}>15 minutes — Gratuit</span>
                </div>
                )}
                {isOralBlanc && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Durée</span>
                  <span className="body-small" style={{ fontWeight: 600 }}>1h15 — Oral blanc</span>
                </div>
                )}
                {isPresentation && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Durée</span>
                  <span className="body-small" style={{ fontWeight: 600 }}>20 minutes — Présentation personnelle</span>
                </div>
                )}
              </div>
              <p className="caption" style={{ marginTop: '1.5rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                Un email de confirmation a été envoyé à votre adresse.
              </p>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                {!isKitBooking && (
                <button className="btn-secondary" style={{ flex: 1 }} onClick={() => { 
                  setBookingConfirmed(null); 
                  setSelectedSlot(null); 
                  setSelectedDate(null); 
                  if (isDirect && directProgramId) {
                    setStep(2);
                  } else {
                    setStep(1); 
                    setSelectedProgram(''); 
                  }
                }}>
                  Réserver une autre séance
                </button>
                )}
                <button className="btn-primary" style={{ flex: 1 }} onClick={() => navigate('/dashboard')}>
                  Mon tableau de bord
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (pageLoading) {
    return (
      <div style={{ paddingTop: '80px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)' }}>Chargement de la page de réservation...</p>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px', minHeight: '100vh' }}>
      {/* Hero */}
      <section style={{ 
        background: 'var(--gradient-hero-warm)',
        padding: '4rem 1.2rem 2rem',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <h1 className="heading-hero" style={{ marginBottom: '1rem' }}>
            {isOralBlanc ? 'Réserver un oral blanc' : isPresentation ? 'Réserver mon RDV de présentation personnelle' : 'Réserver un créneau gratuit'}
          </h1>
          <p className="body-large">
            Échangez {isKitBooking ? '15' : isOralBlanc ? '75' : '20'} minutes avec votre accompagnatrice pour {isOralBlanc ? 'simuler votre oral blanc dans les conditions réelles' : isPresentation ? 'travailler la trame de votre présentation personnelle' : 'faire le point sur votre projet VAE'}
          </p>
        </div>
      </section>

      {/* Candidate Info Form */}
      <div className="container" style={{ maxWidth: '900px', padding: '2rem 1.2rem 0' }}>
        {!candidateInfoSubmitted ? (
          <div className="card" style={{ padding: '2rem' }} data-testid="candidate-info-form">
            <h3 className="heading-3" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
              Vos informations
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label className="body-small" style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>Nom <span style={{ color: '#DC2626' }}>*</span></label>
                <input
                  type="text"
                  data-testid="input-nom"
                  value={candidateInfo.nom}
                  onChange={(e) => setCandidateInfo(prev => ({ ...prev, nom: e.target.value }))}
                  placeholder="Votre nom"
                  style={{
                    width: '100%', padding: '0.75rem 1rem', borderRadius: '10px',
                    border: '2px solid var(--border-primary)', background: 'var(--bg-card)', fontSize: '0.9rem'
                  }}
                />
              </div>
              <div>
                <label className="body-small" style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>Prénom <span style={{ color: '#DC2626' }}>*</span></label>
                <input
                  type="text"
                  data-testid="input-prenom"
                  value={candidateInfo.prenom}
                  onChange={(e) => setCandidateInfo(prev => ({ ...prev, prenom: e.target.value }))}
                  placeholder="Votre prénom"
                  style={{
                    width: '100%', padding: '0.75rem 1rem', borderRadius: '10px',
                    border: '2px solid var(--border-primary)', background: 'var(--bg-card)', fontSize: '0.9rem'
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label className="body-small" style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>Email <span style={{ color: '#DC2626' }}>*</span></label>
                <input
                  type="email"
                  data-testid="input-email"
                  value={candidateInfo.email}
                  onChange={(e) => setCandidateInfo(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="votre@email.fr"
                  style={{
                    width: '100%', padding: '0.75rem 1rem', borderRadius: '10px',
                    border: '2px solid var(--border-primary)', background: 'var(--bg-card)', fontSize: '0.9rem'
                  }}
                />
              </div>
              <div>
                <label className="body-small" style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>Téléphone <span style={{ color: '#DC2626' }}>*</span></label>
                <input
                  type="tel"
                  data-testid="input-telephone"
                  value={candidateInfo.telephone}
                  onChange={(e) => setCandidateInfo(prev => ({ ...prev, telephone: e.target.value }))}
                  required
                  placeholder="06 12 34 56 78"
                  style={{
                    width: '100%', padding: '0.75rem 1rem', borderRadius: '10px',
                    border: `2px solid ${!candidateInfo.telephone ? '#fca5a5' : 'var(--border-primary)'}`, background: 'var(--bg-card)', fontSize: '0.9rem'
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label className="body-small" style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>Livret 1 déjà déposé ?</label>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  {['Oui', 'Non'].map(val => (
                    <button
                      key={val}
                      type="button"
                      data-testid={`livret1-${val.toLowerCase()}`}
                      onClick={() => setCandidateInfo(prev => ({ ...prev, livret1Depose: val }))}
                      style={{
                        flex: 1, padding: '0.75rem', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600,
                        cursor: 'pointer', transition: 'all 0.2s',
                        border: candidateInfo.livret1Depose === val ? '2px solid var(--text-primary)' : '2px solid var(--border-primary)',
                        background: candidateInfo.livret1Depose === val ? 'var(--text-primary)' : 'var(--bg-card)',
                        color: candidateInfo.livret1Depose === val ? 'white' : 'var(--text-secondary)'
                      }}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="body-small" style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>Rédaction du livret 2 débutée ?</label>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  {['Oui', 'Non'].map(val => (
                    <button
                      key={val}
                      type="button"
                      data-testid={`redaction-${val.toLowerCase()}`}
                      onClick={() => setCandidateInfo(prev => ({ ...prev, redactionDebutee: val }))}
                      style={{
                        flex: 1, padding: '0.75rem', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600,
                        cursor: 'pointer', transition: 'all 0.2s',
                        border: candidateInfo.redactionDebutee === val ? '2px solid var(--text-primary)' : '2px solid var(--border-primary)',
                        background: candidateInfo.redactionDebutee === val ? 'var(--text-primary)' : 'var(--bg-card)',
                        color: candidateInfo.redactionDebutee === val ? 'white' : 'var(--text-secondary)'
                      }}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="body-small" style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>Ce dont j'ai besoin</label>
              <textarea
                data-testid="input-besoin"
                value={candidateInfo.besoin}
                onChange={(e) => setCandidateInfo(prev => ({ ...prev, besoin: e.target.value }))}
                placeholder="Décrivez brièvement vos besoins (ex: aide à la rédaction du livret 2, préparation à l'oral...)"
                rows={3}
                style={{
                  width: '100%', padding: '0.75rem 1rem', borderRadius: '10px',
                  border: '2px solid var(--border-primary)', background: 'var(--bg-card)',
                  fontSize: '0.9rem', resize: 'vertical', fontFamily: 'inherit'
                }}
              />
            </div>
            <button
              className="btn-primary"
              data-testid="submit-candidate-info"
              disabled={!candidateInfo.nom || !candidateInfo.prenom || !candidateInfo.email || !candidateInfo.telephone || !candidateInfo.livret1Depose || !candidateInfo.redactionDebutee || !candidateInfo.besoin}
              onClick={() => setCandidateInfoSubmitted(true)}
              style={{ width: '100%', justifyContent: 'center', opacity: (!candidateInfo.nom || !candidateInfo.prenom || !candidateInfo.email || !candidateInfo.telephone || !candidateInfo.livret1Depose || !candidateInfo.redactionDebutee || !candidateInfo.besoin) ? 0.5 : 1 }}
            >
              Valider mes informations
            </button>
          </div>
        ) : (
          <div style={{
            background: 'var(--accent-green-200)',
            border: '2px solid #4CAF50',
            borderRadius: '1rem',
            padding: '1rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap'
          }} data-testid="candidate-info-summary">
            <Check size={24} style={{ color: '#4CAF50' }} />
            <span className="body-medium" style={{ flex: 1 }}>
              <strong>{candidateInfo.prenom} {candidateInfo.nom}</strong> — {candidateInfo.email} · {candidateInfo.telephone}
            </span>
            <button
              onClick={() => setCandidateInfoSubmitted(false)}
              className="btn-secondary"
              style={{ padding: '0.5rem 1rem', fontSize: '0.75rem' }}
              data-testid="edit-candidate-info"
            >
              Modifier
            </button>
          </div>
        )}
      </div>

      {/* Progress Steps */}
      <section style={{ padding: '2rem 1.2rem' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '3rem',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: '20px',
              left: '10%',
              right: '10%',
              height: '2px',
              background: 'var(--border-light)',
              zIndex: 0
            }} />
            
            {[1, 2, 3].map((num) => (
              <div key={num} style={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: 1,
                position: 'relative',
                zIndex: 1
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: step >= num ? 'var(--text-primary)' : 'var(--bg-card)',
                  border: `2px solid ${step >= num ? 'var(--text-primary)' : 'var(--border-primary)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: step >= num ? 'white' : 'var(--text-muted)',
                  fontWeight: 600,
                  marginBottom: '0.5rem'
                }}>
                  {step > num ? <Check size={20} /> : num}
                </div>
                <span className="caption" style={{ textAlign: 'center' }}>
                  {num === 1 && 'Formation'}
                  {num === 2 && 'Date'}
                  {num === 3 && 'Horaire'}
                </span>
              </div>
            ))}
          </div>

          {/* Step 1: Select Program */}
          {step === 1 && (
            <div>
              <h2 className="heading-2" style={{ marginBottom: '2rem', textAlign: 'center' }}>
                Choisissez votre formation VAE
              </h2>
              <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                <select
                  data-testid="booking-program-select"
                  value={selectedProgram}
                  onChange={(e) => handleProgramSelect(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '1rem 1.25rem',
                    fontSize: '1rem',
                    borderRadius: '12px',
                    border: '2px solid var(--border-primary)',
                    background: 'var(--bg-card)',
                    cursor: 'pointer',
                    appearance: 'none',
                    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23666\' stroke-width=\'2\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'%3E%3C/polyline%3E%3C/svg%3E")',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 1rem center'
                  }}
                >
                  <option value="">— Sélectionnez un diplôme —</option>
                  {VAE_PROGRAMS.map(prog => (
                    <option key={prog.id} value={prog.id}>
                      {prog.title} — {prog.fullName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Step 2: Select Date */}
          {step === 2 && program && (
            <div>
              <h2 className="heading-2" style={{ marginBottom: '1rem', textAlign: 'center' }}>
                Choisissez une date
              </h2>
              <p className="body-small" style={{ 
                textAlign: 'center',
                marginBottom: '2rem',
                color: 'var(--text-secondary)'
              }}>
                Formation : {program.title}<br />
                <span className="caption">Les créneaux sont disponibles à partir de 48h après aujourd'hui</span>
              </p>
              <div style={{ 
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '2rem'
              }}>
                <div className="card" style={{ display: 'inline-block' }}>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={(date) => date < minimumDate}
                    locale={fr}
                    className="rounded-md"
                  />
                </div>
              </div>
              <p className="body-small" style={{ textAlign: 'center', color: '#92400e', marginTop: '1rem' }}>
                Aucun créneau ne vous convient ? Contactez <strong>Rose</strong> pour trouver une solution.
              </p>
            </div>
          )}

          {/* Step 3: Select Time Slot */}
          {step === 3 && selectedDate && (
            <div>
              <h2 className="heading-2" style={{ marginBottom: '1rem', textAlign: 'center' }}>
                Choisissez un horaire
              </h2>
              <p className="body-small" style={{ 
                textAlign: 'center',
                marginBottom: '2rem',
                color: 'var(--text-secondary)'
              }}>
                {selectedDate.toLocaleDateString('fr-FR', { 
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                gap: '0.5rem',
                marginBottom: '2rem',
                maxHeight: '400px',
                overflowY: 'auto',
                padding: '0.5rem'
              }}>
                {getAvailableSlotsForDate().map((slot) => (
                  <button
                    key={slot.id}
                    onClick={() => handleSlotSelect(slot)}
                    disabled={!slot.available}
                    className={selectedSlot?.id === slot.id ? 'btn-primary' : 'btn-secondary'}
                    style={{
                      opacity: slot.available ? 1 : 0.5,
                      cursor: slot.available ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      flexDirection: slot.label ? 'column' : 'row',
                      padding: slot.label ? '0.5rem 0.75rem' : undefined,
                      borderColor: slot.label ? '#dc262640' : undefined
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Clock size={16} />
                      <span>{slot.time}</span>
                    </div>
                    {slot.label && (
                      <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#DC2626', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        {slot.label}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {getAvailableSlotsForDate().length === 0 && (
                <p className="body-medium" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                  Aucun créneau disponible pour cette date. Veuillez choisir une autre date.
                </p>
              )}

              {/* Message de contact */}
              <div style={{
                marginTop: '1.5rem', padding: '1rem 1.5rem', borderRadius: '12px',
                background: '#fffbeb', border: '1px solid #fbbf24', textAlign: 'center'
              }} data-testid="contact-rose-message">
                <p className="body-small" style={{ color: '#92400e', lineHeight: 1.6 }}>
                  Aucun créneau ne vous convient ? Pas de souci !<br />
                  Contactez <strong>Rose</strong> pour lui donner vos disponibilités et une solution sera trouvée, pas d'inquiétude.
                </p>
              </div>

              {/* Booking Summary */}
              {selectedSlot && (
                <div className="card" style={{ 
                  background: 'var(--bg-section)',
                  marginTop: '2rem'
                }}>
                  <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
                    Récapitulatif de votre réservation
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span className="body-small" style={{ color: 'var(--text-muted)' }}>Formation :</span>
                      <span className="body-small" style={{ fontWeight: 500 }}>{program.title}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span className="body-small" style={{ color: 'var(--text-muted)' }}>Date :</span>
                      <span className="body-small" style={{ fontWeight: 500 }}>
                        {selectedDate.toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span className="body-small" style={{ color: 'var(--text-muted)' }}>Heure :</span>
                      <span className="body-small" style={{ fontWeight: 500 }}>{selectedSlot.time}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.75rem', borderTop: '1px solid var(--border-light)' }}>
                      <span className="body-small" style={{ color: 'var(--text-muted)' }}>Durée :</span>
                      <span className="body-small" style={{ fontWeight: 600, color: '#16a34a' }}>{isKitBooking ? '15' : isOralBlanc ? '75' : '20'} minutes — {isOralBlanc ? 'Oral blanc' : isPresentation ? 'Présentation personnelle' : 'Gratuit'}</span>
                    </div>
                  </div>
                  <button 
                    onClick={handleBooking}
                    className="btn-primary"
                    style={{ width: '100%', justifyContent: 'center' }}
                    disabled={bookingLoading}
                    data-testid="confirm-booking-btn"
                  >
                    {bookingLoading ? 'Réservation en cours...' : 'Confirmer la réservation'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          {step > 1 && (
            <div style={{ 
              display: 'flex',
              justifyContent: 'center',
              marginTop: '2rem'
            }}>
              <button
                onClick={() => {
                  if (isDirect && step === 2) {
                    navigate('/dashboard?tab=bookings');
                  } else {
                    setStep(step - 1);
                  }
                }}
                className="btn-secondary"
              >
                Retour
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
